import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SideBarDef,
} from 'ag-grid-community';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { ShipmentService } from 'src/app/services/modules/shipments/shipment.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SavedSearchComponent } from 'src/app/components/saved-search/saved-search.component';

@Component({
  selector: 'app-shipment-listing-v2',
  encapsulation: ViewEncapsulation.Emulated,
  templateUrl: './shipment-listing-v2.component.html',
  styleUrls: [
    '../shipment-listing/shipment-listing.component.scss',
  ],
})
export class ShipmentListingV2Component implements OnInit {
  public selectedStatus: string[] = [];
  public statuses!: any[];
  public columnDefs: ColDef[] = [];
  public gridApi: any;
  public showSelect = true;
  public viewFilterType = 'listing';
  public module = 'shipment';
  public paginationPageSize = 20;
  public paginationPageSizeSelector: number[] | boolean = [10, 20, 50, 100];
  public customerId: number | null = null;

  @ViewChild('savedSearch') savedSearch!: SavedSearchComponent;

  public defaultColDef: ColDef = {
    width: 130,
    filter: true,
    floatingFilter: true,
    resizable: true,
    sortable: true,
  };

  public sideBar: SideBarDef | string | string[] | boolean | null = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel: 'filters',
    hiddenByDefault: true,
  };

  public gridOptions: GridOptions = {
    sideBar: this.sideBar,
    rowModelType: 'serverSide',
    pagination: true,
    paginationPageSize: this.paginationPageSize,
    paginationAutoPageSize: false,
    cacheBlockSize: this.paginationPageSize,
    maxBlocksInCache: 10,
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    animateRows: true,
    getRowId: (params) => String(params.data?.listing_id ?? params.data?.id),
  };

  constructor(
    private shipmentService: ShipmentService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.columnDefs = this.shipmentService.transformListingColumnV2();

    this.authService.getUser().subscribe((res: any) => {
      if (res.status === 'success') {
        this.customerId = res.data.customer_id;
      }
    });

    this.selectAllForDropdownItems(this.getStatus());
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.setSideBarVisible(true);
    this.gridApi.setGridOption('serverSideDatasource', this.createDatasource());
    if (this.savedSearch) this.savedSearch.refresh(this.gridApi);
  }

  private createDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const payload = {
          startRow: params.request.startRow ?? 0,
          endRow: params.request.endRow ?? this.paginationPageSize,
          sortModel: params.request.sortModel ?? [],
          filterModel: params.request.filterModel ?? {},
          status_array: this.getNormalizedStatuses(),
        };

        this.shipmentService.getCustomerListingV2(payload).pipe(take(1)).subscribe({
          next: (res: any) => {
            const rowData = Array.isArray(res?.rows) ? res.rows : [];
            const rowCount =
              typeof res?.rowCount === 'number' && res.rowCount >= 0
                ? res.rowCount
                : typeof res?.meta?.recordsFiltered === 'number'
                  ? res.meta.recordsFiltered
                : typeof res?.lastRow === 'number' && res.lastRow >= 0
                    ? res.lastRow
                    : undefined;

            params.success({
              rowData,
              rowCount,
            });
          },
          error: () => {
            params.fail();
          },
        });
      },
    };
  }

  private checkFromDashboard(): void {
    this.route.queryParams.subscribe((params) => {
      if (!params['filter']) {
        return;
      }

      const filterValue = String(params['filter']).toLowerCase();
      this.clearUrlParams();

      if (this.savedSearch) {
        this.savedSearch.clearFilter().finally(() => {
          this.applyStatusFilter(filterValue);
        });
        return;
      }

      this.applyStatusFilter(filterValue);
    });
  }

  private clearUrlParams(): void {
    this.router.navigate(['.'], { relativeTo: this.route, queryParams: {} });
  }

  private getNormalizedStatuses(): string[] {
    const isSelectedAll = this.selectedStatus.includes('All Statuses');

    if (!this.selectedStatus.length || isSelectedAll || this.selectedStatus.length === this.statuses?.length) {
      return ['All'];
    }

    return this.selectedStatus;
  }

  applyStatusFilter(filterValue: string): void {
    this.showSelect = false;

    setTimeout(() => {
      if (!this.statuses) return;

      const status = this.statuses.find(
        (item: any) => String(item.value).toLowerCase() === filterValue
      );

      this.selectedStatus = status?.value ? [status.value] : [];
      this.showSelect = true;
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: true });
      }
    });
  }

  getStatus() {
    this.shipmentService
      .fetchStatus(this.viewFilterType)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.statuses = res.data;
        this.checkFromDashboard();
      });

    return this.statuses;
  }

  statusLabelName(v: any) {
    if (v.name) return v.name;
    if (v.value) return v.value;
    return 'All Statuses';
  }

  selectAllForDropdownItems(items: any[]) {
    if (!items) return;

    items.forEach((element: { [x: string]: string }) => {
      element['selectedAllGroup'] = 'All Statuses';
    });
  }

  compareAccounts = (item: any, selected: any) => {
    if (selected.selectedAllGroup && item.selectedAllGroup) {
      return item.selectedAllGroup === selected.selectedAllGroup;
    }

    return false;
  };

  onSelectionChange(): void {
    if (this.gridApi) {
      this.gridApi.refreshServerSide({ purge: true });
    }
  }

  onPaginationChanged(): void {
    if (!this.gridApi) return;

    const newPageSize = this.gridApi.paginationGetPageSize();
    if (newPageSize === this.paginationPageSize) return;

    this.paginationPageSize = newPageSize;
    this.gridApi.setGridOption('cacheBlockSize', newPageSize);
    this.gridApi.refreshServerSide({ purge: true });
  }

  clearFiltersExternally(): void {
    this.selectedStatus = [];
    if (this.gridApi) {
      this.gridApi.refreshServerSide({ purge: true });
    }
  }

  setSideBarVisible(value: boolean): void {
    this.gridApi?.setSideBarVisible(value);
  }

  eventHandlingListener(event: any): void {
    if (this.savedSearch) {
      this.savedSearch.handleGridEvent(event);
    }
  }

  onColumnVisible(event: any): void {
    if (this.savedSearch) {
      this.savedSearch.handleGridEvent(event);
    }
  }
}
